import { Button, CardProps, Stack, Text } from '@chakra-ui/react'
import { ConsumableAttemptBillingSummaryCard } from 'features/common/ConsumableAttempt/views/ConsumableAttemptBillingSummaryCard'
import { usePropertyDetailsStore } from 'features/PropertyDetails/infra/react/usePropertyDetailsState'
import { useSkiptraceStore } from 'features/Skiptrace/infra/react/Skiptrace.state'
import { DateLib } from 'libs/Date'
import { Card } from 'presentation/components/molecules/Card'
import { useLeadId } from 'presentation/libs/LeadIdContext'
import { useHasMoreSkiptracePlans } from 'presentation/screens/SkiptraceScreen/hooks/useHasMoreSkiptracePlans'
import { mbp } from 'presentation/utils/mapBreakpoint'
import { useEffect, useRef, useState } from 'react'

export type SkiptraceBillingSummaryCardProps = {
  isColumnCard?: boolean
  onCancel?: () => void
} & CardProps

export const SkiptraceBillingSummaryCard = (props: SkiptraceBillingSummaryCardProps) => {
  const {
    onCancel,
    isColumnCard,
    ...cardProps
  } = props

  const state = useSkiptraceBillingSummaryCardState()
  const shouldLoadPlanOptions = state.status === 'ready'
    && state.fundsInfo.status !== 'sufficient-consumables'
  const shouldRefreshRepeatAttemptFunds = state.status === 'ready'
    && state.hasExistingResult
    && !state.hasConfirmationKey
    && state.fundsInfo.status !== 'sufficient-consumables'
  const { hasMoreSkiptracePlans, loading: plansLoading } = useHasMoreSkiptracePlans(shouldLoadPlanOptions)

  // Track if this is the initial render
  const didInitialSetup = useRef(false)
  const didRefreshRepeatAttemptFunds = useRef(false)

  // Track whether to show the payment confirmation modal
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false)

  // Auto-proceed if user has sufficient consumables
  useEffect(() => {
    // Only run this once when state is ready, data is loaded, and we haven't done setup yet
    if (
      state.status === 'ready'
      && !plansLoading
      && !didInitialSetup.current
      && !shouldRefreshRepeatAttemptFunds
    ) {
      didInitialSetup.current = true

      if (
        state.fundsInfo.status === 'sufficient-consumables'
        && (!state.hasExistingResult || state.hasConfirmationKey)
      ) {
        // Auto-confirm and skip confirmation if free skiptraces are available
        state.performSkiptrace()
        if (onCancel) onCancel()
      }
    }
  }, [
    onCancel,
    plansLoading,
    shouldRefreshRepeatAttemptFunds,
    state,
  ])

  useEffect(() => {
    if (!shouldRefreshRepeatAttemptFunds || didRefreshRepeatAttemptFunds.current) return

    didRefreshRepeatAttemptFunds.current = true
    state.refreshConfirmationKey()
  }, [shouldRefreshRepeatAttemptFunds, state])

  // Don't show any modals until loading is complete
  if (state.status === 'unready' || plansLoading) return null

  // If we have sufficient consumables, don't show any modal (it will be auto-confirmed)
  if (state.status === 'ready' && state.fundsInfo.status === 'sufficient-consumables')
    return null

  const availableFunds = state.fundsInfo.funds.available
  const pricePenniesPerUnit = state.fundsInfo.price
  const nextRenewalDateRaw = state.fundsInfo.consumablesRefreshDate
  const nextRenewalDate = DateLib.formatMMDDYYDots(nextRenewalDateRaw)
  const currentPlan = state.fundsInfo.planName

  // Handler for proceeding to payment confirmation
  const handleProceedToPayment = () => {
    setShowPaymentConfirmation(true)
  }

  // Handle upgrading plan
  const handleUpgrade = () => {
    // Navigate to the pricing page with skiptraces section
    window.location.href = '/pricing#skiptraces'
    if (onCancel) onCancel()
  }

  // For the payment confirmation modal
  if (showPaymentConfirmation) {
    return (
      <ConsumableAttemptBillingSummaryCard
        title='Skiptrace'
        subtitle=''
        consumableType='skiptrace'
        paymentPrice={pricePenniesPerUnit}
        pricePenniesPerUnit={pricePenniesPerUnit}
        refreshingConsumables={state.fundsInfo.refreshingConsumables}
        remainingConsumables={state.fundsInfo.remainingConsumables}
        consumablesRefreshDate={nextRenewalDate}
        currentPlan={state.fundsInfo.planName}
        freeItemExceedCount={undefined}
        availableFunds={availableFunds}
        fundsStatus={state.fundsInfo.status}
        onAttempt={state.performSkiptrace}
        isProcessing={state.isSkiptracing}
        onCancel={onCancel}
        isColumnCard={isColumnCard}
        quantity={1}
        {...cardProps}
      />
    )
  }

  // For the "out of free skiptraces" modal with upgrade options
  if (state.fundsInfo.status !== 'sufficient-consumables' && hasMoreSkiptracePlans) {
    return (
      <Card
        variant='default'
        colorScheme='modal.highlight'
        size={mbp({
          mobSm: 'xs-locked',
          mob: 'sm-locked',
          tabSm: 'lg-locked',
          dtLg: isColumnCard ? 'sm-locked' : 'lg-locked',
        })}
        maxW='736px'
        {...cardProps}
      >
        <Text
          textStyle='h2'
          color='graystrong.500'
        >
          Out of Free Skiptraces
        </Text>

        <Text
          mt={mbp({ mobSm: 2, mob: 3, tabSm: 2, dtLg: 2 })}
          textStyle='bodyXLFat'
          color='graystrong.200'
        >
          You&apos;ve run out of free skiptraces for this month
          {currentPlan
            ? (
              <>
                {' '}
                on your
                {' '}
                {currentPlan}
                {' '}
                plan.
              </>
            )
            : '.'}
        </Text>

        <Text
          mt={2}
          textStyle='bodyL'
          color='graystrong.200'
        >
          You can either pay for individual skiptraces or upgrade your plan for additional free skiptraces each month.
        </Text>

        <Stack
          justifyContent='center'
          gap={3}
          mt={mbp({ mobSm: 3, mob: 4, tabSm: 5 })}
          direction={{
            base: 'column',
            tabSm: 'row',
          }}
        >
          <Button
            variant='outline'
            colorScheme='neutral'
            size='lg'
            onClick={onCancel}
            order={{
              base: '3',
              tabSm: '1',
            }}
          >
            Cancel
          </Button>
          <Button
            variant='outline'
            colorScheme='positive'
            size='lg'
            onClick={handleProceedToPayment}
            order={{
              base: '2',
              tabSm: '2',
            }}
          >
            Pay for Skiptrace
          </Button>
          <Button
            w={mbp({ mobSm: 'auto', mob: 'auto' })}
            variant='solid'
            colorScheme='positive'
            size='lg'
            onClick={handleUpgrade}
            order={{
              base: '1',
              tabSm: '3',
            }}
          >
            Upgrade Plan
          </Button>
        </Stack>
      </Card>
    )
  }

  // Default case: standard billing card for payment-only option
  return (
    <ConsumableAttemptBillingSummaryCard
      title='Skiptrace'
      subtitle=''
      consumableType='skiptrace'
      paymentPrice={pricePenniesPerUnit}
      pricePenniesPerUnit={pricePenniesPerUnit}
      refreshingConsumables={state.fundsInfo.refreshingConsumables}
      remainingConsumables={state.fundsInfo.remainingConsumables}
      consumablesRefreshDate={nextRenewalDate}
      currentPlan={state.fundsInfo.planName}
      freeItemExceedCount={undefined}
      availableFunds={availableFunds}
      fundsStatus={state.fundsInfo.status}
      onAttempt={state.performSkiptrace}
      isProcessing={state.isSkiptracing}
      onCancel={onCancel}
      isColumnCard={isColumnCard}
      quantity={1}
      {...cardProps}
    />
  )
}

const useSkiptraceBillingSummaryCardState = () => {
  const routeLeadId = useLeadId()
  const propDetailsState = usePropertyDetailsStore(propertyDetailsApi => {
    const propertyState = propertyDetailsApi.actions.getProperty.state
    const doesPropertyMatch = propertyState.status === 'success'
      && propertyState.data.leadId === routeLeadId

    const ownerAddress = propertyState.status === 'success'
      && propertyState.data.status === 'with-details'
      && propertyState.data.ownership?.address

    if (!doesPropertyMatch || !ownerAddress) return { status: 'unready' as const }

    return {
      status: 'ready' as const,
      leadId: propertyState.data.leadId,
      ownerAddress,
    }
  })

  const fundsState = useSkiptraceStore(skiptraceApi => {
    const skiptraceFundsState = skiptraceApi.entities.skiptraceFunds

    if (skiptraceFundsState.status !== 'success') return { status: 'unready' as const }

    return {
      status: 'ready' as const,
      fundsInfo: {
        funds: skiptraceFundsState.attempt.funds,
        price: skiptraceFundsState.attempt.price,
        consumablesRefreshDate: skiptraceFundsState.attempt.consumablesRefreshDate,
        refreshingConsumables: skiptraceFundsState.attempt.refreshingConsumables,
        remainingConsumables: skiptraceFundsState.attempt.remainingConsumables,
        planName: skiptraceFundsState.attempt.planName,
        status: skiptraceFundsState.attempt.status,
      },
      hasConfirmationKey: !!skiptraceFundsState.attempt.confirmationKey,
    }
  })

  const skiptraceStore = useSkiptraceStore(skipApi => {
    if (propDetailsState.status !== 'ready') return { status: 'unready' as const }

    const hasExistingResult = skipApi.entities.resultState.status === 'success'
      && !!skipApi.entities.resultState.result

    return {
      status: 'ready' as const,
      isSkiptracing: skipApi.entities.resultState.status === 'skiptracing',
      hasExistingResult,
      performSkiptrace: () => {
        void skipApi.actions.performSkiptrace.execute({
          leadId: propDetailsState.leadId,
          address: propDetailsState.ownerAddress,
        })
      },
      refreshConfirmationKey: () => {
        void skipApi.actions.getSkiptraceFundsInfo.execute({
          leadId: propDetailsState.leadId,
          address: propDetailsState.ownerAddress,
        })
      },
    }
  })

  const isAnyUnready = propDetailsState.status === 'unready'
    || fundsState.status === 'unready'
    || skiptraceStore.status === 'unready'

  if (isAnyUnready) return { status: 'unready' as const }

  return {
    ...fundsState,
    status: 'ready' as const,
    isSkiptracing: skiptraceStore.isSkiptracing,
    hasExistingResult: skiptraceStore.hasExistingResult,
    hasConfirmationKey: fundsState.hasConfirmationKey,
    performSkiptrace: skiptraceStore.performSkiptrace,
    refreshConfirmationKey: skiptraceStore.refreshConfirmationKey,
  }
}
